import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Chip, CircularProgress, Alert, TablePagination, Breadcrumbs, Link, Button,
    TextField, InputAdornment
} from '@mui/material';
import { Refresh, DirectionsBike, ArrowBack, Search } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getPartnerBookings } from '../../services/Api';

const GetAllPartnerBookings = () => {
    const { partnerId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [bookings, setBookings] = useState([]);
    const [partnerType, setPartnerType] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination & Search
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    const partnerName = location.state?.partner?.name || 'Partner';

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 1000);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchBookings = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getPartnerBookings(partnerId, page + 1, rowsPerPage, debouncedSearch);
            if (response.success) {
                const data = response.data || {};
                setBookings(data.bookings || []);
                setPartnerType(data.partnerType || '');
                setTotalRows(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || response.totalRecords || 0 || 0);
            } else {
                setError(response.message || 'Failed to fetch bookings');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred while fetching bookings');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBookings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, partnerId, debouncedSearch]);

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link underline="hover" color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/partners'); }}>
                        Partners
                    </Link>
                    <Typography color="text.primary">Bookings List</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton onClick={() => navigate('/partners')} sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <ArrowBack />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 300, display: 'flex', alignItems: 'center' }}>
                                <DirectionsBike sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
                                {partnerName}'s Bookings
                            </Typography>
                            {partnerType && (
                                <Chip
                                    label={`Role: ${partnerType}`}
                                    size="small"
                                    color="secondary"
                                    sx={{ mt: 1, fontWeight: 700, borderRadius: 1 }}
                                />
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder="Search by Booking ID..."
                            size="small"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                width: 250,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '20px',
                                    backgroundColor: 'rgba(0,0,0,0.02)',
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <IconButton
                            onClick={fetchBookings}
                            disabled={loading}
                            sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider' }}
                        >
                            <Refresh />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Booking #</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Payment Method</TableCell>

                                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : bookings.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">No bookings found for this partner</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                bookings.map((booking, index) => (
                                    <TableRow key={booking.id || booking._id} hover>
                                        <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>{booking.bookingNumber || `#${booking.id}`}</TableCell>
                                        <TableCell>{booking.user?.name || '-'}</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>₹{booking.grandTotal || '0'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.status || 'Pending'}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    textTransform: 'capitalize',
                                                    ...(booking.status?.toLowerCase() === 'completed' || booking.status?.toLowerCase() === 'delivered' ? {
                                                        bgcolor: 'success.light', color: 'success.dark'
                                                    } : booking.status?.toLowerCase() === 'cancelled' ? {
                                                        bgcolor: 'error.light', color: 'error.dark'
                                                    } : {
                                                        bgcolor: 'warning.light', color: 'warning.dark'
                                                    })
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.paymentMethod?.toUpperCase() || 'N/A'}
                                                size="small"
                                                sx={{
                                                    fontWeight: 600,
                                                    ...(booking.paymentMethod?.toLowerCase() === 'online' ? {
                                                        bgcolor: 'info.light', color: 'info.dark'
                                                    } : booking.paymentMethod?.toLowerCase() === 'cod' ? {
                                                        bgcolor: 'warning.light', color: 'warning.dark'
                                                    } : {
                                                        bgcolor: 'grey.200', color: 'grey.700'
                                                    })
                                                }}
                                            />
                                        </TableCell>

                                        <TableCell>{booking.createdAt ? new Date(booking.createdAt).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => navigate(`/partners/bookings/details/${booking.id}`, { state: { booking } })}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                View Details
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
};

export default GetAllPartnerBookings;
